import imageCompression from 'browser-image-compression'
import { apiService } from './api'

export interface ImageUploadOptions {
  maxSize?: number // in bytes
  allowedTypes?: string[]
  resize?: {
    maxWidth: number
    maxHeight: number
    quality?: number
    targetSizeMB?: number
  }
}

export interface ImageUploadResult {
  url: string
  fileName: string
  size: number
  type: string
  userId: string
  guid: string
}

export class ImageUploadService {
  private static instance: ImageUploadService
  
  public static getInstance(): ImageUploadService {
    if (!ImageUploadService.instance) {
      ImageUploadService.instance = new ImageUploadService()
    }
    return ImageUploadService.instance
  }

  private defaultOptions: Record<'icon' | 'cover' | 'screenshot' | 'avatar', ImageUploadOptions> = {
    icon: {
      maxSize: 2 * 1024 * 1024, // 2MB
      allowedTypes: ['image/jpeg', 'image/png', 'image/webp'],
      resize: {
        maxWidth: 512,
        maxHeight: 512,
        quality: 0.85,
        targetSizeMB: 0.5
      }
    },
    cover: {
      maxSize: 10 * 1024 * 1024, // 10MB
      allowedTypes: ['image/jpeg', 'image/png', 'image/webp'],
      resize: {
        maxWidth: 1600,
        maxHeight: 900,
        quality: 0.85,
        targetSizeMB: 0.5
      }
    },
    screenshot: {
      maxSize: 5 * 1024 * 1024, // 5MB
      allowedTypes: ['image/jpeg', 'image/png', 'image/webp'],
      resize: {
        maxWidth: 1920,
        maxHeight: 1080,
        quality: 0.85,
        targetSizeMB: 2.5
      }
    },
    avatar: {
      maxSize: 2 * 1024 * 1024, // 2MB
      allowedTypes: ['image/jpeg', 'image/png', 'image/webp'],
      resize: {
        maxWidth: 256,
        maxHeight: 256,
        quality: 0.85,
        targetSizeMB: 0.3
      }
    }
  }

  /**
   * Upload an image via backend (with moderation)
   * Unified method for all image types (project icons/covers/screenshots and avatars)
   */
  async uploadImage(
    file: File, 
    entityId: string, 
    type: 'icon' | 'cover' | 'screenshot' | 'avatar',
    onProgress?: (progress: number) => void
  ): Promise<ImageUploadResult> {
    console.log(`🖼️ Starting image upload for entity ${entityId}, type: ${type}`)
    
    const options = this.defaultOptions[type]
    
    // Validate file before any processing
    this.validateFile(file, options)
    
    // Optimize image if needed
    const optimizedFile = options.resize 
      ? await this.optimizeImage(file, options.resize, options.maxSize)
      : file

    if (options.maxSize && optimizedFile.size > options.maxSize) {
      const maxSizeMB = (options.maxSize / 1024 / 1024).toFixed(1)
      throw new Error(`Compressed image still exceeds the maximum size of ${maxSizeMB}MB. Please choose a smaller image.`)
    }
    
    // Create FormData for upload
    const formData = new FormData()
    formData.append('file', optimizedFile)
    formData.append('entityId', entityId)
    formData.append('type', type)
    
    console.log(`📤 Uploading to backend for moderation and storage...`)
    
    try {
      // Upload through backend (includes moderation)
      const response = await apiService.post('/images/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total && onProgress) {
            const percentComplete = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            onProgress(percentComplete)
          }
        },
      })
      
      const result = response.data
      
      if (!result.success || !result.url) {
        throw new Error(result.error || 'Upload failed')
      }
      
      console.log(`✅ Image uploaded successfully: ${result.url}`)
      
      return {
        url: result.url,
        fileName: result.fileName,
        size: result.size,
        type: result.type,
        userId: result.userId,
        guid: result.guid
      }
      
    } catch (error: any) {
      console.error('❌ Image upload failed:', error)
      
      // Handle moderation errors with user-friendly messages
      if (error.response?.data?.code?.startsWith('MODERATION_FAILED_')) {
        const message = error.response.data.error || 'Image contains inappropriate content and cannot be uploaded'
        throw new Error(message)
      }
      
      // Handle other errors
      const errorMessage = error.response?.data?.error || error.message || 'Upload failed'
      throw new Error(errorMessage)
    }
  }

  /**
   * Delete an image from GCS via backend API
   */
  async deleteImage(url: string): Promise<void> {
    try {
      console.log(`🗑️ Deleting image: ${url}`)
      
      await apiService.delete('/images/delete', { data: { url } })
      
      console.log(`✅ Image deleted successfully`)
    } catch (error) {
      console.warn('⚠️ Failed to delete image:', error)
      // Don't throw error for delete failures as the image might already be deleted
    }
  }

  /**
   * Validate file against upload options
   */
  private validateFile(file: File, options: ImageUploadOptions): void {
    // Check file size
    if (options.maxSize && file.size > options.maxSize) {
      const maxSizeMB = (options.maxSize / 1024 / 1024).toFixed(1)
      throw new Error(`File too large. Maximum size allowed: ${maxSizeMB}MB`)
    }

    // Check file type
    if (options.allowedTypes && !options.allowedTypes.includes(file.type)) {
      const allowedTypesStr = options.allowedTypes.map(type => type.split('/')[1].toUpperCase()).join(', ')
      throw new Error(`Invalid file type. Allowed types: ${allowedTypesStr}`)
    }

    // Additional validation
    if (file.size === 0) {
      throw new Error('File is empty')
    }
  }


  /**
   * Compress and resize image using browser-image-compression
   */
  private async optimizeImage(
    file: File,
    resizeOptions: NonNullable<ImageUploadOptions['resize']>,
    maxSize?: number
  ): Promise<File> {
    try {
      const sizeLimitMB = maxSize ? maxSize / (1024 * 1024) : undefined
      const targetSizeMB = (() => {
        if (typeof resizeOptions.targetSizeMB === 'number' && sizeLimitMB) {
          return Math.min(resizeOptions.targetSizeMB, sizeLimitMB)
        }
        return resizeOptions.targetSizeMB ?? sizeLimitMB
      })()

      const compressionOptions = {
        maxSizeMB: targetSizeMB,
        maxWidthOrHeight: Math.max(resizeOptions.maxWidth, resizeOptions.maxHeight),
        initialQuality: Math.min(0.95, Math.max(0.1, resizeOptions.quality ?? 0.85)),
        useWebWorker: true,
        fileType: file.type,
        alwaysKeepResolution: false,
        maxIteration: 15
      }

      const compressed = await imageCompression(file, compressionOptions)
      const compressedFile = compressed instanceof File
        ? compressed
        : new File([compressed], file.name, {
            type: (compressed as Blob).type || file.type,
            lastModified: Date.now()
          })

      console.log(
        `📦 Image compressed: ${(file.size / 1024 / 1024).toFixed(2)}MB → ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`
      )
      return compressedFile
    } catch (error) {
      console.warn('⚠️ Image compression failed, using original file.', error)
      return file
    }
  }

  /**
   * Generate a preview URL for a file (for immediate display before upload)
   */
  generatePreviewUrl(file: File): string {
    return URL.createObjectURL(file)
  }

  /**
   * Cleanup preview URL to prevent memory leaks
   */
  cleanupPreviewUrl(url: string): void {
    URL.revokeObjectURL(url)
  }

  /**
   * Get image dimensions from file
   */
  async getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image()
      
      img.onload = () => {
        resolve({ width: img.width, height: img.height })
        URL.revokeObjectURL(img.src)
      }
      
      img.onerror = () => {
        reject(new Error('Failed to load image'))
        URL.revokeObjectURL(img.src)
      }
      
      img.src = URL.createObjectURL(file)
    })
  }

}

// Export singleton instance
export const imageUploadService = ImageUploadService.getInstance() 
